import { isSameCountry } from './locationUtils';

/**
 * Aggregate parcels using a linear stack method resembling real carrier aggregation.
 * Extracts parcel info from items, standardizes units to cm/kg, 
 * computes total actual weight, max dimensions, and total height.
 * Returns the chargeable weight (max of actual vs volumetric).
 */
export const aggregateParcels = (items) => {
  if (!items || items.length === 0) return 0;

  let totalWeight = 0;
  let maxWidth = 0;
  let maxLength = 0;
  let sumHeight = 0;

  for (const item of items) {
    const pInfo = item?.parcelInfo || item?.product?.parcelInfo;
    if (!pInfo) continue;

    let itemLength = parseFloat(pInfo.length) || 0;
    let itemWidth = parseFloat(pInfo.width) || 0;
    let itemHeight = parseFloat(pInfo.height) || 0;
    let itemWeight = parseFloat(pInfo.weight) || 0;

    // Safety check
    if (itemLength <= 0 || itemWidth <= 0 || itemHeight <= 0 || itemWeight <= 0) continue;

    const itemDistanceUnit = pInfo.units?.distance?.toLowerCase() || 'cm';
    const itemMassUnit = pInfo.units?.mass?.toLowerCase() || 'kg';
    const itemQuantity = item.quantity || 1;

    // Convert to aggregate units (cm, kg)
    if (itemDistanceUnit === 'in') {
      itemLength *= 2.54; itemWidth *= 2.54; itemHeight *= 2.54;
    }

    if (itemMassUnit === 'lb') { itemWeight *= 0.453592; }
    else if (itemMassUnit === 'oz') { itemWeight *= 0.0283495; }
    else if (itemMassUnit === 'g') { itemWeight /= 1000; }

    totalWeight += (itemWeight * itemQuantity);
    if (itemLength > maxLength) maxLength = itemLength;
    if (itemWidth > maxWidth) maxWidth = itemWidth;
    sumHeight += (itemHeight * itemQuantity);
  }

  // Calculate volumetric weight: (L x W x H in cm) / 5000
  const volumetricWeightKg = (maxLength * maxWidth * sumHeight) / 5000;

  // Chargeable weight is the maximum of actual flat weight and volumetric weight
  const chargeableWeightKg = Math.max(totalWeight, volumetricWeightKg);

  return chargeableWeightKg;
};

/**
 * Calculate estimated cost based on distance and international status and weight
 * @param {number} distanceInMeters - Distance in meters
 * @param {boolean} isInternational - Whether this is international shipping
 * @param {number} insuranceCoverage - Insurance coverage amount
 * @param {number} chargeableWeightKg - The calculated chargeable weight in kg
 * @returns {number} - Estimated shipping cost
 */
export const calculateEstimatedCost = (distanceInMeters, isInternational = false, insuranceCoverage = 0, chargeableWeightKg = 0) => {
  // Distance in kilometers
  const distanceInKm = distanceInMeters / 1000;

  // New Formula: base + distance_rate + weight_rate
  const baseRate = isInternational ? 19.99 : 5.99;
  let distanceRate = distanceInKm * Math.min(0.02, isInternational ? 0.04 : 0.02); // Just keeping a slight cap to not blow up cross country
  let weightRate = chargeableWeightKg * 1.2;

  let cost = baseRate + distanceRate + weightRate;

  let insuranceFee = 0;
  if (insuranceCoverage > 0) {
    insuranceFee = insuranceCoverage * 0.01;
    cost += insuranceFee;
  }


  // Cap at reasonable maximum - higher for international
  // However, do not cap the insurance fee, only the base+distance+weight shipping part.
  const cappedShipping = Math.min(cost - insuranceFee, isInternational ? 59.99 : 29.99);
  return cappedShipping + insuranceFee;
};

/**
 * Determine if shipping is international based on address/location data
 * @param {Object} fromAddress - Seller address 
 * @param {Object} toAddress - Buyer address or location
 * @returns {boolean} - Whether shipping is international
 */
export const isInternationalShipping = (fromAddress, toAddress) => {
  if (!fromAddress || !toAddress) return false;

  // Get country data from either code or name
  const fromCountry = fromAddress.countryCode || fromAddress.country;
  const toCountry = toAddress.countryCode || toAddress.country;

  // If either country is missing, we can't determine if it's international
  if (!fromCountry || !toCountry) {
    // Default to domestic
    return false;
  }

  // Use the isSameCountry utility to properly compare countries
  const isSame = isSameCountry(fromCountry, toCountry);


  return !isSame;
};

/**
 * Estimate delivery time based on distance and international status
 * @param {number} distanceInMeters - Distance in meters
 * @param {boolean} isInternational - Whether this is international shipping
 * @returns {string} - Estimated delivery time range (e.g., "2-4 days")
 */
export const estimateDeliveryTime = (distanceInMeters, isInternational = false) => {
  if (isInternational) {
    // International shipping times are longer
    if (distanceInMeters > 8000000) {
      return '10-15 days'; // Very long international (e.g., US to Asia)
    } else if (distanceInMeters > 5000000) {
      return '7-10 days';  // Medium international (e.g., US to Europe)
    } else {
      return '5-7 days';   // Shorter international (e.g., US to Canada/Mexico)
    }
  } else {
    // Domestic shipping times
    if (distanceInMeters > 1000000) {
      return '4-6 days';   // Cross-country
    } else if (distanceInMeters > 500000) {
      return '2-4 days';   // Regional
    } else {
      return '1-2 days';   // Local
    }
  }
};

/**
 * Format distance for display using appropriate units
 * @param {number} meters - Distance in meters
 * @param {Object} userLocation - User location with country code
 * @param {Object} sellerAddress - Seller address with country code
 * @returns {string} - Formatted distance string
 */
export const formatDistance = (meters, userLocation, sellerAddress) => {
  if (meters === null) return '';

  // Check if international shipping
  const isInternational =
    sellerAddress?.country && userLocation?.country &&
    sellerAddress.country !== userLocation.country;

  // Use miles for US, km otherwise
  const inMiles = meters / 1609.34;
  const inKm = meters / 1000;

  // For very short distances
  if (inMiles < 1) {
    return `${Math.round(inMiles * 5280)} feet away`;
  }

  // If either address is in US, show miles, otherwise km
  const useMetric = userLocation?.country !== 'US' && sellerAddress?.country !== 'US';

  if (useMetric) {
    return `${Math.round(inKm)} km away`;
  } else {
    return `${Math.round(inMiles)} miles away`;
  }
};